/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Empty,
  Space,
  Spin,
  Select,
  Typography,
  Segmented,
} from 'antd';
import { TableOutlined, AppstoreOutlined, ThunderboltOutlined, DownloadOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CreateBatchEventModal from '../../components/events/CreateBatchEventModal';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import MainLayout from '../../components/common/MainLayout';
import { useEventQueuePermissions } from '../../hooks/useEventQueuePermissions';
import { hasAction } from '../../types/permissions';
import {
  useGetPlantingsQuery,
  useCreatePlantingMutation,
  useUpdatePlantingMutation,
} from '../../api/plantingsApi';
import { useGetCropByIdQuery } from '../../api/cropsApi';
import type { Planting, PlantingLifecycleStatus, PlantingPayload, PlantingUpdatePayload } from '../../models/Planting';
import { plantingStatusOptions } from '../../features/plantings/constants';
import PlantingFormModal from '../../features/plantings/components/PlantingFormModal';
import PlantingsList, { PlantingsViewMode } from '../../components/plantings/PlantingsList';
import { toastError, toastSuccess } from '../../utils/toast';
import { useRefreshWorld, useSave, useWorld } from '../../contexts/WorldContext';
import { buildScenarioWorldIndexes, findScenarioCropSummary } from '../../utils/scenarioWorldAdapters';
import { getPlantingDisplayQuantity } from '../../components/plantings/utils/plantingsFormatters';

const { Title, Text } = Typography;

const styles = {
  titleNoMargin: css`
    margin: 0;
  `,
  italicSecondary: css`
    font-size: 14px;
  `,
  selectMinWidth: css`
    min-width: 200px;
  `,
};

const ScenarioFarmCropPlantingsPage: React.FC = () => {
  const { saveId, cropId } = useParams<{ saveId: string; cropId: string }>();
  const { t } = useTranslation('crops');
  const world = useWorld();
  const refreshWorld = useRefreshWorld();
  const indexes = useMemo(() => buildScenarioWorldIndexes(world.structure), [world.structure]);
  const crop = useMemo(
    () => findScenarioCropSummary(world.structure, cropId, indexes, world.runtime?.plantings),
    [cropId, indexes, world.runtime?.plantings, world.structure],
  );
  const [statusFilter, setStatusFilter] = useState<PlantingLifecycleStatus | undefined>();
  const [viewMode, setViewMode] = useState<PlantingsViewMode>('table');

  const plantings = useMemo(() => {
    const items = crop?.plantings || [];
    if (!statusFilter) return items;
    return items.filter((planting) => planting.lifecycleStatus === statusFilter);
  }, [crop?.plantings, statusFilter]);

  if (!crop) {
    return (
      <MainLayout>
        <Card>{t('details.notFound')}</Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">{t('nav.farms')}</Link> },
            { title: <Link to={`/saves/${saveId}`}>{world.scenario?.title || t('nav.scenario')}</Link> },
            { title: <Link to={`/saves/${saveId}/crops`}>{t('nav.crops')}</Link> },
            { title: crop.name },
            { title: t('details.plantings') },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Alert
          type="info"
          showIcon
          message={t('cropPlantings.scenario.title')}
          description={t('cropPlantings.scenario.description')}
        />

        <Card>
          <Space direction="vertical" size={4}>
            <Title level={4} css={styles.titleNoMargin}>{crop.name}</Title>
            <Text type="secondary">
              {t('cropPlantings.scenario.totals', { plantings: crop.plantingsCount || 0, units: crop.totalQuantity })}
            </Text>
          </Space>
        </Card>

        <Card
          title={t('cropPlantings.listTitle', { count: plantings.length })}
          extra={
            <Space wrap>
              <Button onClick={() => { void refreshWorld(); }}>{t('common:actions.refresh')}</Button>
              <Select
                allowClear
                placeholder={t('cropPlantings.allStatuses')}
                options={plantingStatusOptions}
                value={statusFilter}
                onChange={(value) => setStatusFilter((value as PlantingLifecycleStatus | undefined) ?? undefined)}
                onClear={() => setStatusFilter(undefined)}
                css={styles.selectMinWidth}
              />
              <Segmented<PlantingsViewMode>
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { label: t('cropPlantings.view.table'), value: 'table', icon: <TableOutlined /> },
                  { label: t('cropPlantings.view.gallery'), value: 'gallery', icon: <AppstoreOutlined /> },
                ]}
              />
            </Space>
          }
        >
          {plantings.length === 0 ? (
            <Empty description={t('cropPlantings.emptyFilter')} />
          ) : (
            <PlantingsList
              plantings={plantings}
              viewMode={viewMode}
              groupByCrop={false}
            />
          )}
        </Card>
      </Space>
    </MainLayout>
  );
};

const FarmBoundCropPlantingsPage: React.FC = () => {
  const { saveId, cropId } = useParams<{ saveId: string; cropId: string }>();
  const { t } = useTranslation('crops');
  const save = useSave();
  const farmId = save.farmId;
  const farm = save.farmId ? { id: save.farmId, name: '' } : null;
  
  // Загружаем информацию о культуре
  const {
    data: crop,
    isLoading: cropLoading,
    isError: cropError,
  } = useGetCropByIdQuery(cropId || skipToken);
  
  // CN-0008: Проверка прав пользователя
  const user = useSelector((state: any) => state.auth.user);
  const { canCreateEvents } = useEventQueuePermissions(farmId || '');  // CN-0012: UUID string
  
  // Проверка прав на создание/редактирование/удаление посадок
  const canCreatePlantings = hasAction(user, 'plantings', 'plantings.create', farmId);  // CN-0012: UUID string
  const canUpdatePlantings = hasAction(user, 'plantings', 'plantings.update', farmId);

  const [statusFilter, setStatusFilter] = useState<PlantingLifecycleStatus | undefined>();
  const [viewMode, setViewMode] = useState<PlantingsViewMode>('table');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPlanting, setEditingPlanting] = useState<Planting | null>(null);
  const [selectedPlantingIds, setSelectedPlantingIds] = useState<string[]>([]);
  const [bulkEventsModalOpen, setBulkEventsModalOpen] = useState(false);

  const plantingsQueryArg = useMemo(() => {
    if (!saveId || !cropId) return skipToken;
    const arg: {
      saveId: string;
      cropId: string;
      limit: number;
      offset: number;
      lifecycleStatus?: PlantingLifecycleStatus;
    } = {
      saveId,
      cropId,
      limit: 200,
      offset: 0,
    };
    if (statusFilter) {
      arg.lifecycleStatus = statusFilter;
    }
    return arg;
  }, [cropId, saveId, statusFilter]);

  const {
    data: plantings = [],
    isLoading: plantingsLoading,
    isError: plantingsError,
    isUninitialized: plantingsUninitialized,
    refetch: refetchPlantings,
  } = useGetPlantingsQuery(plantingsQueryArg);

  const [createPlanting, { isLoading: creatingPlanting }] = useCreatePlantingMutation();
  const [updatePlanting, { isLoading: updatingPlanting }] = useUpdatePlantingMutation();

  useEffect(() => {
    setStatusFilter(undefined);
    setEditingPlanting(null);
    setCreateModalOpen(false);
    setEditModalOpen(false);
  }, [farmId, cropId]);

  const handleOpenCreateModal = () => {
    if (!farmId) {
      toastError(t('cropPlantings.errors.selectFarm'));
      return;
    }
    setEditingPlanting(null);
    setCreateModalOpen(true);
  };

  const handleOpenEditPlanting = (planting: Planting) => {
    setEditingPlanting(planting);
    setEditModalOpen(true);
  };

  const handleCreatePlantingSubmit = async (payload: PlantingPayload | PlantingUpdatePayload) => {
    try {
      if (!payload.locations?.length) {
        throw new Error(t('cropPlantings.errors.noLocations'));
      }
      // Добавляем cropId в payload, если он не указан
      const finalPayload = {
        ...(payload as PlantingPayload),
        cropId: payload.cropId || cropId,
      };
      await createPlanting({ ...finalPayload, saveId: saveId || '' }).unwrap();
      toastSuccess(t('cropPlantings.toasts.created'));
      setCreateModalOpen(false);
      refetchPlantings();
    } catch (error) {
      toastError(error);
    }
  };

  const handleUpdatePlantingSubmit = async (payload: PlantingUpdatePayload) => {
    if (!editingPlanting) return;
    try {
      await updatePlanting({ saveId: saveId || '', id: editingPlanting.id, payload }).unwrap();
      toastSuccess(t('cropPlantings.toasts.updated'));
      setEditModalOpen(false);
      setEditingPlanting(null);
      refetchPlantings();
    } catch (error) {
      toastError(error);
    }
  };

  const handleExportPricelist = () => {
    if (plantings.length === 0) {
      toastError(t('cropPlantings.export.empty'));
      return;
    }

    // Формируем CSV
    const headers = [
      'ID',
      t('cropPlantings.export.columns.crop'),
      t('cropPlantings.export.columns.latinName'),
      t('cropPlantings.export.columns.quantity'),
      t('cropPlantings.export.columns.status'),
      t('cropPlantings.export.columns.location'),
      t('cropPlantings.export.columns.createdAt'),
    ];
    
    const rows = plantings.map((p) => {
      const location = p.locations?.[0];
      const bedName = location?.bed
        ? t('cropPlantings.export.bed', { code: location.bed.anchor_code ?? '' })
        : '—';
      
      return [
        p.id,
        p.crop?.name || '—',
        p.crop?.latinName || '—',
        getPlantingDisplayQuantity(p),
        p.lifecycleStatus,
        bedName,
        p.createdAt?.split('T')[0] || '—',
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    // Скачиваем файл
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pricelist_crop_${cropId}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toastSuccess(t('cropPlantings.export.done', { count: plantings.length }));
  };

  const farmName = farm?.name ?? t('nav.farmWithId', { id: farmId ?? '' });
  const cropName = crop?.name ?? t('cropPlantings.cropWithId', { id: cropId ?? '' });
  const skipQueries = !farmId || !cropId;

  if (cropLoading) {
    return (
      <MainLayout
        breadcrumb={
          <Breadcrumb
            items={[
              { title: <Link to="/">{t('nav.farms')}</Link> },
              { title: <Link to={`/saves/${saveId}`}>{farmName}</Link> },
              { title: <Link to={`/saves/${saveId}/plantings`}>{t('details.plantings')}</Link> },
              { title: '...' },
            ]}
          />
        }
      >
        <Card><Spin /></Card>
      </MainLayout>
    );
  }

  if (cropError || !crop) {
    return (
      <MainLayout
        breadcrumb={
          <Breadcrumb
            items={[
              { title: <Link to="/">{t('nav.farms')}</Link> },
              { title: <Link to={`/saves/${saveId}`}>{farmName}</Link> },
              { title: <Link to={`/saves/${saveId}/plantings`}>{t('details.plantings')}</Link> },
              { title: t('cropPlantings.error') },
            ]}
          />
        }
      >
        <Card>{t('details.notFound')}</Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">{t('nav.farms')}</Link> },
            { title: <Link to={`/saves/${saveId}`}>{farmName}</Link> },
            { title: <Link to={`/saves/${saveId}/plantings`}>{t('details.plantings')}</Link> },
            { title: cropName },
          ]}
        />
      }
    >
      {skipQueries ? (
        <Card>{t('cropPlantings.farmOrCropNotFound')}</Card>
      ) : (
        <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
          <Card>
            <Space direction="vertical" size={4}>
              <Title level={4} css={styles.titleNoMargin}>
                {crop.name}
              </Title>
              {crop.latinName && (
                <Text type="secondary" italic css={styles.italicSecondary}>
                  {crop.latinName}
                </Text>
              )}
              <Text type="secondary">
                {t('cropPlantings.subtitle', { name: crop.name })}
              </Text>
            </Space>
          </Card>

          <Card
            title={t('cropPlantings.listTitle', { count: plantings.length })}
            extra={
              <Space wrap>
                {canCreatePlantings && (
                  <Button type="primary" onClick={handleOpenCreateModal}>
                    {t('cropPlantings.create')}
                  </Button>
                )}
                {canCreateEvents && (
                  <Button
                    type="default"
                    icon={<ThunderboltOutlined />}
                    onClick={() => setBulkEventsModalOpen(true)}
                    disabled={selectedPlantingIds.length === 0}
                  >
                    {selectedPlantingIds.length > 0
                      ? t('cropPlantings.bulkEventsCount', { count: selectedPlantingIds.length })
                      : t('cropPlantings.bulkEvents')}
                  </Button>
                )}
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExportPricelist}
                  disabled={plantings.length === 0}
                >
                  {t('cropPlantings.export.button')}
                </Button>
                <Button onClick={() => refetchPlantings()}>{t('common:actions.refresh')}</Button>
                <Select
                  allowClear
                  placeholder={t('cropPlantings.allStatuses')}
                  options={plantingStatusOptions}
                  value={statusFilter}
                  onChange={(value) => setStatusFilter((value as PlantingLifecycleStatus | undefined) ?? undefined)}
                  onClear={() => setStatusFilter(undefined)}
                  css={styles.selectMinWidth}
                />
                <Segmented<PlantingsViewMode>
                  value={viewMode}
                  onChange={setViewMode}
                  options={[
                    { label: t('cropPlantings.view.table'), value: 'table', icon: <TableOutlined /> },
                    { label: t('cropPlantings.view.gallery'), value: 'gallery', icon: <AppstoreOutlined /> },
                  ]}
                />
              </Space>
            }
          >
            {plantingsLoading && !plantingsUninitialized ? (
              <Spin />
            ) : plantingsError ? (
              <Card type="inner">{t('cropPlantings.loadFailed')}</Card>
            ) : (
              <PlantingsList
                plantings={plantings}
                loading={plantingsLoading}
                viewMode={viewMode}
                farmId={farmId}
                groupByCrop={false}
                onEdit={canUpdatePlantings ? handleOpenEditPlanting : undefined}
                rowSelection={canCreateEvents ? {
                  type: 'checkbox',
                  selectedRowKeys: selectedPlantingIds,
                  onChange: (selectedRowKeys: React.Key[]) => {
                    setSelectedPlantingIds(selectedRowKeys as string[]);
                  },
                } : undefined}
              />
            )}
          </Card>
        </Space>
      )}

      <PlantingFormModal
        open={createModalOpen}
        title={t('cropPlantings.newPlanting')}
        confirmText={t('common:actions.create')}
        loading={creatingPlanting}
        farmId={farmId}
        defaultCropId={cropId}
        onCancel={() => setCreateModalOpen(false)}
        onSubmit={handleCreatePlantingSubmit}
      />

      <PlantingFormModal
        open={editModalOpen}
        title={t('cropPlantings.editPlanting')}
        confirmText={t('common:actions.save')}
        loading={updatingPlanting}
        farmId={farmId}
        initialPlanting={editingPlanting}
        onCancel={() => {
          setEditModalOpen(false);
          setEditingPlanting(null);
        }}
        onSubmit={handleUpdatePlantingSubmit}
      />

      <CreateBatchEventModal
        open={bulkEventsModalOpen}
        onClose={() => setBulkEventsModalOpen(false)}
        farmId={farmId || ''}
        plantingIds={selectedPlantingIds}
        onSuccess={() => {
          setSelectedPlantingIds([]);
          setBulkEventsModalOpen(false);
          refetchPlantings();
        }}
      />
    </MainLayout>
  );
};

const FarmCropPlantingsPage: React.FC = () => {
  const save = useSave();
  if (save.type === 'scenario') {
    return <ScenarioFarmCropPlantingsPage />;
  }
  return <FarmBoundCropPlantingsPage />;
};

export default FarmCropPlantingsPage;
